import { useMemo } from "react";
import { Users, MessageSquare } from "lucide-react";
import { formatElapsedMin } from "@/utils/time";
import useAppStore from "@/store/useAppStore";

/**
 * CR-0012 — unified 11px typography scale; action bar fixed height;
 * elapsed string always visible above the bar.
 *
 * Phase G (Δ-11) — subscribes to `useAppStore.tick` so the elapsed
 * string re-derives every second. Per C-17, only the `tick` selector
 * and the `table` prop drive renders.
 *
 * CR-0025 (Δ-23, 2026-06-01) — the grey "Clear" bar stays visible on
 * the card but is **non-interactive**. The dialog's Clear button
 * (`dlg-btn-clear` inside `OccupiedForm`) is the only actionable
 * clear path. Tap on the card opens the Occupied dialog as usual.
 */
export default function OccupiedCard({ table }) {
  const tick = useAppStore((s) => s.tick);

  const guestCount = (table.adult || 0) + (table.children || 0);
  const elapsedDisplay = useMemo(
    () => formatElapsedMin(table.occupied_time),
    // tick intentionally in deps: forces recompute every second.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [table.occupied_time, tick]
  );
  const locationLabel =
    table.location_type === "indoor"
      ? "IN"
      : table.location_type === "outdoor"
      ? "OUT"
      : null;
  const hasComment = !!table.comments?.trim();

  return (
    <div
      data-testid={`occupied-card-${table.code}`}
      className="flex flex-col h-full px-2.5 py-1.5 overflow-hidden"
    >
      {/* Row 1: people count + elapsed time */}
      <div className="flex items-start justify-between min-w-0">
        <div className="flex items-center gap-1 min-w-0">
          <Users size={11} style={{ color: "#424242" }} />
          <span
            className="text-[11px] font-semibold truncate leading-none"
            style={{ color: "#424242" }}
          >
            {guestCount}
          </span>
        </div>
        <span
          data-testid={`elapsed-time-${table.code}`}
          className="text-[11px] truncate ml-1 leading-none"
          style={{ color: "#424242" }}
        >
          {elapsedDisplay || "0 Min"}
        </span>
      </div>

      {/* Row 2: table_number + comment icon */}
      <div className="flex items-center justify-between min-w-0 mt-1 min-h-[12px]">
        <span
          className="text-[11px] truncate leading-none"
          style={{ color: "#424242" }}
        >
          {table.table_number || ""}
        </span>
        {hasComment && (
          <MessageSquare
            data-testid={`comment-icon-${table.code}`}
            size={11}
            style={{ color: "#6B7280" }}
          />
        )}
      </div>

      {/* Location */}
      {locationLabel && (
        <div className="text-center mt-1">
          <span
            className="text-[11px] font-medium leading-none"
            style={{ color: "#424242" }}
          >
            {locationLabel}
          </span>
        </div>
      )}

      {/* Code badge — centered, 11px */}
      <div className="flex items-center justify-center my-auto min-h-0">
        <div
          data-testid={`code-badge-${table.code}`}
          className="px-2.5 py-0.5 rounded-full max-w-full"
          style={{ backgroundColor: "#D0D0D0" }}
        >
          <span
            className="text-[11px] font-bold truncate block leading-tight"
            style={{ color: "#424242" }}
          >
            {table.code}
          </span>
        </div>
      </div>

      {/* Clear bar — fixed height; never shrinks.
          CR-0025 (Δ-23): static visual only — no onClick, no role, no
          tabIndex, no hover/cursor classes. Dialog Clear is the only
          actionable path. */}
      <div
        data-testid={`clear-bar-${table.code}`}
        className="mt-1 flex items-center justify-center rounded-md py-1 shrink-0"
        style={{ backgroundColor: "#888888" }}
      >
        <span className="text-[11px] font-bold text-white leading-none">
          Clear
        </span>
      </div>
    </div>
  );
}
